import React from 'react';
import {ReactComponent as FootballFieldIcon} from '../../assets/svg/field_icon.svg';
import {ReactComponent as InfoIcon} from './icons/outline-info.svg';
import * as Popover from '@radix-ui/react-popover';
import {IconButton} from '../elements';
import {LiveBadge} from './LiveBadge';
import {FixtureElement, MatchStatusElement} from "../../models/api/codegen/code";
import {useDispatch, useSelector} from "react-redux";
import {ReactComponent as WatchIcon} from './icons/stop_watch.svg';
import {SET_WIDGET_ID} from "../../store/spirs/actions";
import {useTranslation} from "react-i18next";
import LogoFavorite from '../CommonSportDisplay/icons/specialFavoriteLogo';
import {useFavorites} from "../../utils/FavoritesUtils";
import {format} from "date-fns";
import clsx from 'clsx';
import {CompetitionWrapper} from "../../utils/CompetitionWrapper";
import {PeriodName} from "../score/PeriodName";
import { Popup } from "../Favorites/FavouritesPopup";
import {StoreType} from "../../store/spirs/types";
import { TruncatedText } from '../custom/TruncatedText';

export const Match: React.FC<{ fixture: FixtureElement }> = ({fixture}) => {
    const {t, i18n, i18n: {language}} = useTranslation();
    const dispatch = useDispatch();

    const {isFavorite, onChange} = useFavorites(fixture.fi, useDispatch(), fixture.f);

    const handleClickOnWidget = React.useCallback(() => dispatch(SET_WIDGET_ID({
        widgetId: fixture.fw,
        configurationId: fixture.wci
    })), [fixture.fw, fixture.wci]);

    const favoritesAmount = useSelector((state: StoreType) => state.spirs.favoritesCount);
    const [isOpenPopup, setIsOpenPopup] = React.useState<boolean>(false);

    const handleFavourites = () => {
        if (!isFavorite && favoritesAmount > 49) {
            openPopup();
        } else {
            onChange();
        }
    }

    const openPopup = () => setIsOpenPopup(true);
    const closePopup = () => setIsOpenPopup(false);

    const FavoriteIcon = React.useMemo(() => (
        <div onClick={handleFavourites}>
                <LogoFavorite checked={isFavorite} size='24'/>
        </div>
    ), [isFavorite]);

    const Icon = React.memo(() => (<>{fixture.s === "LIVE" ? FavoriteIcon : <WatchIcon/>}</>));

    const Id = React.memo(
        () =>
            (<TruncatedText style={{color: fixture.sc}}
                            className={`text-[16px] text-center tracking-[0.8px] tablet:w-[75px] w-[56px] font-medium`}
                            text={fixture.fi}
            />));

    const Competitors = React.memo(() => {
        const matchStatus = fixture.ms || {homeScore: 0, awayScore: 0};
        const colorScore = (mainScore?: number, secondScore?: number) => {
            if (fixture.s === "LIVE" || fixture.s === "DELAYED") {
                return "text-[#FFFFFF]"
            }
            if (mainScore === undefined || secondScore === undefined) {
                return "text-[#FFFFFF]"
            }
            if (mainScore > secondScore) {
                return "text-[#FFDB4D]"
            } else if (mainScore < secondScore) {
                return "text-[#ABABAB]"
            } else {
                return "text-[#FFFFFF]"
            }
        };

        return (
            <CompetitionWrapper
                fixtureId={fixture.fi}
                fixtureStatus={fixture.s}
                element={
                    <div className="flex flex-col justify-center">
                        <TruncatedText
                            text={fixture.c.length < 1 ? fixture.cn : fixture.c[0].n}
                            className={clsx(
                                'p-[5px] tablet:text-[16px] text-nowrap overflow-hidden max-[366px]:w-[100px] w-[120px] notebook:w-[220px]',
                                colorScore(
                                    matchStatus.homeScore,
                                    matchStatus.awayScore
                                )
                            )}
                            isCursorPointer
                        />
                        {fixture.c.length === 2 && <TruncatedText
                            text={fixture.c[1].n}
                            className={clsx(
                                'p-[5px] tablet:text-[16px] text-nowrap overflow-hidden max-[366px]:w-[100px] w-[120px] notebook:w-[220px]',
                                colorScore(
                                    matchStatus.homeScore,
                                    matchStatus.awayScore
                                )
                            )}
                            isCursorPointer
                        />}
                    </div>
                }
            />
        );
    });

    const SportBetLink = React.memo(() => {
        if (!fixture.su) {
            return <React.Fragment/>
        }
        const i18nUrl = fixture.su + "&lang=" + language;
        return (<a href={i18nUrl} target={"_blank"}>{t("fixture.sportBetLink")}</a>);
    })

    const Score = React.memo(() => {
        const scores = fixture.ms && fixture.ms.periodScores && fixture.ms.periodScores
            .sort((a, b) => {
                if (!a || !b) return 0;
                return (a.periodNumber || 0) > (b.periodNumber || 0) ? 1 : -1
            });

        return (
            <div className="w-[10ch] tablet:w-[21ch] flex flex-col justify-between overflow-y-hidden py-[5px] h-full">
                <div className="flex text-[16px] font-bold gap-[1ch]">
                    <div className={'text-[#FFDB4D]'}>
                        {fixture.ms && fixture.ms.homeScore}
                    </div>
                    {scores && scores.map((score) => (
                            <div>{score.homeScore}</div>
                        ))}
                </div>

                <div className="flex text-[16px] font-bold gap-[1ch]">
                    <div className={'text-[#FFDB4D]'}>
                        {fixture.ms && fixture.ms.awayScore}
                    </div>
                    {scores && scores.map((score) => (
                        <div>{score.awayScore}</div>
                    ))}
                </div>
            </div>
        )
    });

    const FinishedInfo= React.memo(() => (<div className='flex flex-col justify-center gap-[10px] max-[367px]:gap-[2px] w-full tablet:bg-[#121518]'>
        {fixture.et && <div className="flex justify-center">
            {format(new Date(fixture.et), 'HH:mm:ss')}
        </div>}
        <div className="flex justify-center ">
            {i18n.exists(`results.${fixture.s}`) && t(`results.${fixture.s}`)}
        </div>
    </div>));

    const LiveInfo = React.memo(() => (<div className='flex flex-col justify-center gap-[10px] max-[367px]:gap-[2px] w-full tablet:bg-[#121518]'>
        {(fixture.ms && fixture.ms.eventClock || fixture.cl) && <div className="flex justify-center">
            {fixture.ms && fixture.ms.eventClock && <div>{fixture.ms.eventClock.eventTime}&prime;</div>}
            {fixture.cl && <SportBetLink/>}
        </div>}
        <div className="flex justify-center">
            <LiveBadge/>
        </div>
    </div>));

    const Info = React.memo(() => (
            fixture.s === 'LIVE' ? <LiveInfo/> : <FinishedInfo/>
    ));

    const WidgetIcon = React.memo(() => (<IconButton onClick={handleClickOnWidget}>
        <FootballFieldIcon className='w-[25px] h-[25px] fill-[#8EA5A1]'/>
    </IconButton>));

    const ExtraIcon = React.memo(() => {
        if (fixture.cl) {
            return <React.Fragment/>
        } else if (fixture.s === 'LIVE' && fixture.fw) {
            return <WidgetIcon/>
        } else {
            return <StatisticIcon statistic={fixture.ms} sportId={fixture.sid}/>;
        }
    });

    return (
        <div className="flex tablet:gap-[60px] gap-[10px] justify-between w-full bg-[#171A1E] mb-[5px] p-[10px] text-[12px] tablet:rounded-[8px] tablet:mb-[2px] ">
            <Popup isOpen={isOpenPopup} onClose={closePopup} fixtureId={fixture.fi} favorite={fixture.f} />
            <div className='flex gap-[5px] flex-row '>
                    <div className='flex flex-col justify-between notebook:justify-start notebook:flex-row notebook:gap-[5px] items-center'>
                        <div className='w-[30px] h-[30px]'>
                            <Icon/>
                        </div>
                        <div className='flex justify-center items-center'>
                            <Id/>
                        </div>
                    </div>
                    <div className='flex flex-col justify-center'>
                        <Competitors/>
                    </div>
                    <div>
                        <Score/>
                    </div>
                </div>

            <div className='flex flex-row max-[367px]:flex-col justify-between w-full min-w-[58px] my-[-10px] tablet:gap-[20px]'>
                <div className='flex justify-center w-full h-full max-w-[264px]'>
                    <Info/>
                </div>
                <div className='flex justify-center items-center'>
                    <ExtraIcon/>
                </div>
            </div>
        </div>
    )
};

const StatisticIcon: React.FC<{ statistic?: MatchStatusElement, sportId: number }> = ({statistic, sportId}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    return (<div className='flex'>
        <Popover.Root>
            <Popover.Trigger asChild>
                {statistic && statistic.periodScores && statistic.periodScores.length === 0 ? null :
                    <IconButton id={id} onClick={handleClick}>
                        <InfoIcon/>
                    </IconButton>
                }
            </Popover.Trigger>
            <Popover.Content>
                {statistic && statistic.periodScores && (
                    <div className="flex flex-col bg-[#272D33] p-[16px] rounded-[12px] border border-[#EACD40] w-[240px] shadow-[0_0_10px_2px_black]">

                        {statistic && statistic.periodScores && statistic.periodScores.filter(period => period.periodNumber !== 100 && period.periodNumber !== 101 && period.periodNumber !== 102)
                            .map((period, i) => (
                                <React.Fragment key={i}>
                                    <div className="flex justify-between">
                                        <div><PeriodName sportId={sportId} periodNumber={period.periodNumber} /></div>
                                        <div>{period.homeScore}:{period.awayScore}</div>
                                    </div>
                                    <hr className="h-[1px] w-full bg-[#5A5C66] border-0"/>
                                </React.Fragment>
                            ))}
                    </div>
                )}
            </Popover.Content>
        </Popover.Root>
    </div>);
};
